// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Config } from "../config";
import {
    AssetUpdateResponse,
    ChunkUploadResponse,
    FileUploadCompleteResponse,
    FinalizedChunkedAssetUploadRequest,
    IndividualAssetsReleaseRequest,
    UpdateAssetsRequest,
    UploadAssetRequest,
} from "./types/assetApiTypes";
import { Asset } from "./types/enterpriseParticipantApiTypes";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;

// Define a service using a base URL and expected endpoints
export const cclTokenedAssetsApi = createApi({
    reducerPath: "cclTokenedAssetsApi",
    tagTypes: ["Assets", "SessionAssets", "Profile"],
    baseQuery: fetchBaseQuery({
        baseUrl: apiManagementRootUrl,
        prepareHeaders: (headers, { getState }) => {
            //we should probably change this.
            const token = localStorage.getItem("token") as string;
            headers.set("authorization", `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        uploadAssetChunk: builder.mutation<ChunkUploadResponse, FormData>({
            query: (data) => ({
                url: "ccl-access2/Assets/chunk-upload",
                method: "POST",
                body: data,
            }),
        }),
        finalizeAssetChunkUpload: builder.mutation<
            FileUploadCompleteResponse,
            FinalizedChunkedAssetUploadRequest
        >({
            query: (body) => ({
                url: "ccl-access2/Assets/finalize-chunk-upload",
                method: "POST",
                body: body,
            }),
            invalidatesTags: (_, __, args: FinalizedChunkedAssetUploadRequest) => [
                { type: "SessionAssets", id: args.properties["ProgramId"] },
            ],
        }),
        finalizeImageChunkUpload: builder.mutation<FileUploadCompleteResponse, FormData>({
            query: (body) => ({
                url: "ccl-access2/chunkupload/finalize-profile-pic",
                method: "POST",
                body: body,
            }),
            //I am unsure of how to invalidate the "GetDemographic" endpoints tags from here. May just need manual refreshing but ill look into it
            invalidatesTags: ["Profile"],
        }),
        uploadAsset: builder.mutation<FileUploadCompleteResponse, UploadAssetRequest>({
            query: (body) => ({
                url: "ccl-access2/Assets/upload",
                method: "POST",
                body: body.formData,
            }),
            invalidatesTags: (_, __, args: UploadAssetRequest) => [
                { type: "SessionAssets", id: args.eskey },
            ],
        }),
        releaseSessionAssets: builder.mutation<AssetUpdateResponse, UpdateAssetsRequest>({
            async queryFn(updateAssetRequest, _queryApi, _extraOptions, baseQuery) {
                const totalKeyCount = updateAssetRequest.aikeys.length;
                const batchSize = 30;

                let rsp: AssetUpdateResponse = {
                    success: true,
                    message: "",
                    assetInfoKeysUpdated: [],
                    assetInfoKeysUpdateFailed: [],
                };

                for (let i = 0; i < totalKeyCount; i = i + batchSize) {
                    const end = i + batchSize < totalKeyCount ? i + batchSize : totalKeyCount;
                    let aikeys = updateAssetRequest.aikeys.slice(i, end);
                    let result = await baseQuery({
                        url: `ccl-access2/Assets/${updateAssetRequest.eskey}/release`,
                        method: "POST",
                        body: aikeys,
                    });
                    if (result != null) {
                        const r = result.data as AssetUpdateResponse;
                        rsp.success = rsp.success && r.success;
                        rsp.message = r.success ? rsp.message : rsp.message + " " + r.message;
                        rsp.assetInfoKeysUpdateFailed = [
                            ...rsp.assetInfoKeysUpdateFailed,
                            ...r.assetInfoKeysUpdateFailed,
                        ];
                        rsp.assetInfoKeysUpdated = [
                            ...rsp.assetInfoKeysUpdated,
                            ...r.assetInfoKeysUpdated,
                        ];
                    }
                }
                return { data: rsp };
            },
            invalidatesTags: (_, __, args: UpdateAssetsRequest) => [
                { type: "SessionAssets", id: args.eskey },
            ],
        }),
        releaseAssets: builder.mutation<AssetUpdateResponse, IndividualAssetsReleaseRequest>({
            async queryFn(releaseIndividualAssetsRequest, _queryApi, _extraOptions, baseQuery) {
                const totalPaxCount = releaseIndividualAssetsRequest.individualAssetLists.length;
                const batchSize = 7;

                let rsp: AssetUpdateResponse = {
                    success: true,
                    message: "",
                    assetInfoKeysUpdated: [],
                    assetInfoKeysUpdateFailed: [],
                };

                for (let i = 0; i < totalPaxCount; i = i + batchSize) {
                    const end = i + batchSize < totalPaxCount ? i + batchSize : totalPaxCount;

                    const subRequest: IndividualAssetsReleaseRequest = {
                        individualAssetLists:
                            releaseIndividualAssetsRequest.individualAssetLists.slice(i, end),
                    };
                    let result = await baseQuery({
                        url: "ccl-access2/Assets/release",
                        method: "POST",
                        body: subRequest,
                    });
                    if (result != null) {
                        const r = result.data as AssetUpdateResponse;
                        rsp.success = rsp.success && r.success;
                        rsp.message = r.success ? rsp.message : rsp.message + " " + r.message;
                        rsp.assetInfoKeysUpdateFailed = [
                            ...rsp.assetInfoKeysUpdateFailed,
                            ...r.assetInfoKeysUpdateFailed,
                        ];
                        rsp.assetInfoKeysUpdated = [
                            ...rsp.assetInfoKeysUpdated,
                            ...r.assetInfoKeysUpdated,
                        ];
                    }
                }
                return { data: rsp };
            },
            invalidatesTags: ["SessionAssets"],
        }),
        removeAssets: builder.mutation<AssetUpdateResponse, UpdateAssetsRequest>({
            async queryFn(updateAssetRequest, _queryApi, _extraOptions, baseQuery) {
                const totalKeyCount = updateAssetRequest.aikeys.length;
                const batchSize = 30;

                let rsp: AssetUpdateResponse = {
                    success: true,
                    message: "",
                    assetInfoKeysUpdated: [],
                    assetInfoKeysUpdateFailed: [],
                };

                for (let i = 0; i < totalKeyCount; i = i + batchSize) {
                    const end = i + batchSize < totalKeyCount ? i + batchSize : totalKeyCount;
                    let aikeys = updateAssetRequest.aikeys.slice(i, end);
                    let result = await baseQuery({
                        url: "ccl-access2/Assets/unrelease",
                        method: "POST",
                        body: aikeys,
                    });
                    if (result != null) {
                        const r = result.data as AssetUpdateResponse;
                        rsp.success = rsp.success && r.success;
                        rsp.message = r.success ? rsp.message : rsp.message + " " + r.message;
                        rsp.assetInfoKeysUpdateFailed = [
                            ...rsp.assetInfoKeysUpdateFailed,
                            ...r.assetInfoKeysUpdateFailed,
                        ];
                        rsp.assetInfoKeysUpdated = [
                            ...rsp.assetInfoKeysUpdated,
                            ...r.assetInfoKeysUpdated,
                        ];
                    }
                }
                return { data: rsp };
            },
            invalidatesTags: (_, __, args: UpdateAssetsRequest) => [
                { type: "SessionAssets", id: args.eskey },
            ],
        }),
        deleteAssets: builder.mutation<AssetUpdateResponse, UpdateAssetsRequest>({
            async queryFn(updateAssetRequest, _queryApi, _extraOptions, baseQuery) {
                const totalKeyCount = updateAssetRequest.aikeys.length;
                const batchSize = 30;

                let rsp: AssetUpdateResponse = {
                    success: true,
                    message: "",
                    assetInfoKeysUpdated: [],
                    assetInfoKeysUpdateFailed: [],
                };

                for (let i = 0; i < totalKeyCount; i = i + batchSize) {
                    const end = i + batchSize < totalKeyCount ? i + batchSize : totalKeyCount;
                    let aikeys = updateAssetRequest.aikeys.slice(i, end);
                    let result = await baseQuery({
                        url: "ccl-access2/Assets/delete",
                        method: "DELETE",
                        body: aikeys,
                    });
                    if (result != null) {
                        const r = result.data as AssetUpdateResponse;
                        rsp.success = rsp.success && r.success;
                        rsp.message = r.success ? rsp.message : rsp.message + " " + r.message;
                        rsp.assetInfoKeysUpdateFailed = [
                            ...rsp.assetInfoKeysUpdateFailed,
                            ...r.assetInfoKeysUpdateFailed,
                        ];
                        rsp.assetInfoKeysUpdated = [
                            ...rsp.assetInfoKeysUpdated,
                            ...r.assetInfoKeysUpdated,
                        ];
                    }
                }
                return { data: rsp };
            },
            invalidatesTags: (_, __, args: UpdateAssetsRequest) => [
                { type: "SessionAssets", id: args.eskey },
            ],
        }),
        getAssetsByImKey: builder.query<Asset[], number>({
            query: (imkey) => `ccl-access2/Assets/by-imkey/${imkey}`,
        }),
        getAssetsBySessionKey: builder.query<Asset[], string>({
            query: (eskey) => `ccl-access2/Assets/by-session-key/${eskey}`,
            providesTags: (_, __, eskey) => [{ type: "SessionAssets", id: eskey }],
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useUploadAssetChunkMutation,
    useFinalizeAssetChunkUploadMutation,
    useFinalizeImageChunkUploadMutation,
    useUploadAssetMutation,
    useReleaseSessionAssetsMutation,
    useReleaseAssetsMutation,
    useRemoveAssetsMutation,
    useDeleteAssetsMutation,
    useLazyGetAssetsByImKeyQuery,
    useGetAssetsBySessionKeyQuery,
    useLazyGetAssetsBySessionKeyQuery,
} = cclTokenedAssetsApi;
